import { useState, useEffect, type JSX } from 'react'
import { History } from 'lucide-react'
import { useAgentStore } from '../../store/agentStore.js'
import type { SessionHistory } from '../../store/agentStore.js'
import { formatInTimezone, timestampMs } from '../../utils/timezone.js'

export default function SessionHistoryTable(): JSX.Element {
  const sessionHistory = useAgentStore((s) => s.sessionHistory)
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  /** `DD/MM/YYYY HH:MM:SS` in the user's selected timezone. */
  const formatDate = (date: Date) =>
    formatInTimezone(
      date,
      {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      },
      'en-GB',
    ).replace(',', '')

  const formatDuration = (seconds: number) => {
    const s = Math.max(0, Math.floor(seconds))
    const hours = Math.floor(s / 3600)
    const minutes = Math.floor((s % 3600) / 60)
    const secs = s % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const getLiveTimes = (entry: SessionHistory, isCurrent: boolean) => {
    if (!isCurrent) {
      return { readyTime: entry.readyTime, availableTime: entry.availableTime, breakTime: entry.breakTime }
    }
    const elapsedSec = Math.max(0, Math.floor((Date.now() - timestampMs(entry.time)) / 1000))
    let readyTime = entry.readyTime
    let availableTime = entry.availableTime
    let breakTime = entry.breakTime
    if (entry.key === 'login') {
      readyTime += elapsedSec
    } else if (entry.key === 'available') {
      availableTime += elapsedSec
    } else if (!['login', 'available', 'logout'].includes(entry.key)) {
      breakTime += elapsedSec
    }
    return { readyTime, availableTime, breakTime }
  }

  const recentHistory = [...sessionHistory].reverse().slice(0, 10)
  const currentEntry = sessionHistory[sessionHistory.length - 1]

  const statusBadgeClass = (key: string) => {
    if (key === 'login') return 'bg-slate-100 text-slate-600 ring-slate-200'
    if (key === 'available') return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    if (key === 'logout') return 'bg-rose-50 text-rose-700 ring-rose-200'
    return 'bg-amber-50 text-amber-700 ring-amber-200'
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-gradient-to-br from-slate-700 to-slate-800 text-white shadow-sm">
          <History className="h-4 w-4" strokeWidth={2} />
        </span>
        <h2 className="text-sm font-semibold tracking-tight text-slate-800">
          Session History
          <span className="ml-2 text-xs font-normal text-slate-400">(Last 10 Records)</span>
        </h2>
      </div>

      <div className="overflow-hidden rounded-[3px] border border-slate-200/80">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50/80 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 whitespace-nowrap">Timestamp</th>
              <th className="px-4 py-3 whitespace-nowrap">User Status</th>
              <th className="px-4 py-3 whitespace-nowrap">Last Status</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Ready Time</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Available Time</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Break Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {recentHistory.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">
                  <div className="mb-3 flex justify-center">
                    <History className="h-7 w-7 text-slate-300" strokeWidth={1.5} />
                  </div>
                  No session history yet
                </td>
              </tr>
            ) : (
              recentHistory.map((entry, index) => {
                const isCurrent = currentEntry === entry
                const live = getLiveTimes(entry, isCurrent)
                return (
                  <tr key={index} className={`transition-colors hover:bg-slate-50/50 ${isCurrent ? 'bg-slate-50/30' : ''}`}>
                    <td className="px-4 py-3 font-mono text-[11px] whitespace-nowrap text-slate-600">
                      {formatDate(entry.time)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-[3px] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ${statusBadgeClass(entry.key)}`}>
                        {entry.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap text-slate-500">
                      {entry.lastStatus || '-'}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-right whitespace-nowrap text-slate-600 tabular-nums">
                      {formatDuration(live.readyTime)}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-right whitespace-nowrap text-slate-600 tabular-nums">
                      {formatDuration(live.availableTime)}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-right whitespace-nowrap text-slate-600 tabular-nums">
                      {formatDuration(live.breakTime)}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] font-medium text-slate-400">
        {sessionHistory.length} record{sessionHistory.length !== 1 ? 's' : ''}
      </p>
    </div>
  )
}
